import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Tooltip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  useTheme,
  useMediaQuery,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useUpdate from "../../../../api/useUpdate";
import NoRecordMessage from "../../../../Components/GeneralComponents/noRecordMessage/NoRecordMessage";
import { useTranslation } from "react-i18next";
import EditPatientDialog from "./EditPatientDialog";
import { getAccess } from "../../../../permissions/Permissions";
import useDelete from "../../../../api/useDelete";
import DeleteIcon from "@mui/icons-material/Delete";
import FormatDateTime from "../../../../Components/GeneralComponents/DateFormatter/FormatDateTime";
import EventIcon from "@mui/icons-material/Event";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

const PatientList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const permissions = getAccess(user?.user?.permissions, user?.user?.isAdmin);
  const token = user?.user?.token || user?.token;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Controlled inputs (instant values)
  const [searchInput, setSearchInput] = useState("");
  const [searchByIDInput, setSearchByIDInput] = useState("");
  const [timeframe, setTimeframe] = useState("all");

  // Debounced states (used for fetching)
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debouncedSearchByID, setDebouncedSearchByID] = useState("");

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const { handleDelete, ConfirmDialog } = useDelete("patients", token);

  // Debounce search input changes
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1); // Reset page on search
    }, 500);

    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchByID(searchByIDInput);
      setPage(1);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchByIDInput]);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    [
      "patients",
      page,
      rowsPerPage,
      debouncedSearch,
      debouncedSearchByID,
      timeframe,
    ],
    `patients/?page=${page}&page_size=${rowsPerPage}&search=${debouncedSearch}&id=${debouncedSearchByID}&timeframe=${timeframe}`,
    token
  );

  const { handleUpdate } = useUpdate("patients", token);

  // Use useCallback to memoize handlers to avoid re-renders
  const handleSearchChange = useCallback((event) => {
    setSearchInput(event.target.value);
  }, []);

  const handleSearchByIDChange = useCallback((event) => {
    setSearchByIDInput(event.target.value);
  }, []);

  const handleTimeframeChange = useCallback((event, newTimeframe) => {
    if (newTimeframe !== null) {
      setTimeframe(newTimeframe);
      setPage(1);
    }
  }, []);

  const handleEditClick = useCallback((patient) => {
    setSelectedPatient(patient);
    setEditOpen(true);
  }, []);

  const handleSaveEdit = useCallback(
    async (updatedPatient) => {
      try {
        await handleUpdate(updatedPatient.id, updatedPatient);
        refetch();
        setEditOpen(false);
      } catch (error) {
        console.error("Error updating patient:", error);
      }
    },
    [handleUpdate, refetch]
  );

  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  }, []);

  return (
    <Grid
      container
      justifyContent="center"
      mb={4}
      sx={{ width: "100%", mt: 3 }}
    >
      <Grid
        item
        xs={12}
        sx={{
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[4],
          borderRadius: 3,
          overflowX: "auto",
          width: "100%",
        }}
      >
        {/* Header & Filters */}
        <Box
          padding={3}
          borderBottom={`1px solid ${theme.palette.divider}`}
          bgcolor={theme.palette.grey[100]}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            color={theme.palette.primary.main}
            mb={1}
          >
            {t("reception.patientList")}
          </Typography>
          <Typography color="text.secondary" mb={2}>
            {data?.count || 0} {t("reception.patients")}
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                placeholder={t("reception.search")}
                value={searchInput}
                onChange={handleSearchChange}
                fullWidth
                size="small"
                variant="outlined"
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                placeholder={t("reception.searchID", "Search By ID")}
                value={searchByIDInput}
                onChange={handleSearchByIDChange}
                fullWidth
                size="small"
                variant="outlined"
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <FilterAltIcon color="action" />
                <ToggleButtonGroup
                  value={timeframe}
                  onChange={handleTimeframeChange}
                  exclusive
                  size="small"
                  aria-label="timeframe filter"
                  sx={{ flexWrap: "wrap" }}
                >
                  <ToggleButton value="all" aria-label="all time">
                    {t("reception.allTime")}
                  </ToggleButton>
                  <ToggleButton value="daily" aria-label="daily">
                    {t("reception.daily")}
                  </ToggleButton>
                  <ToggleButton value="weekly" aria-label="weekly">
                    {t("reception.weekly")}
                  </ToggleButton>
                  <ToggleButton value="monthly" aria-label="monthly">
                    {t("reception.monthly")}
                  </ToggleButton>
                  <ToggleButton value="yearly" aria-label="yearly">
                    {t("reception.yearly")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Patient Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
          <Table
            sx={{ minWidth: 1000 }}
            aria-label="patient table"
            size={isSmallScreen ? "small" : "medium"}
          >
            <TableHead
              sx={{
                backgroundColor: theme.palette.grey[200],
                "& th": {
                  fontWeight: "bold",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell align="left">
                  {t("reception.name", "Name")}
                </TableCell>
                <TableCell>{t("reception.gender")}</TableCell>
                <TableCell>{t("reception.age")}</TableCell>
                <TableCell>{t("reception.phone")}</TableCell>
                <TableCell>{t("reception.bloodType")}</TableCell>
                <TableCell align="left">{t("reception.passport")}</TableCell>
                <TableCell align="left">{t("reception.address")}</TableCell>
                <TableCell>{t("reception.dosiaFileId")}</TableCell>
                <TableCell align="left">
                  {t("reception.date", "Registration Date")}
                </TableCell>
                <TableCell align="left">
                  {t("reception.referBy", "Refer By")}
                </TableCell>
                <TableCell>{t("reception.action")}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={12} sx={{ py: 6, textAlign: "center" }}>
                    <CircularProgress color="primary" size={36} />
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={12}
                    sx={{
                      py: 6,
                      textAlign: "center",
                      color: theme.palette.error.main,
                    }}
                  >
                    {t("reception.errorLoading")}
                  </TableCell>
                </TableRow>
              ) : data?.results?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} sx={{ py: 6, textAlign: "center" }}>
                    <NoRecordMessage message={t("reception.noRecords")} />
                  </TableCell>
                </TableRow>
              ) : (
                data?.results?.map((item) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {item.id}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        maxWidth: 180,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Tooltip title={item.firstname}>
                        <span>
                          {item.firstname} {item.lastname}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {item?.gender === 1 ? "Male" : "Female"}
                    </TableCell>

                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {item.age}
                    </TableCell>

                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {item.phone}
                    </TableCell>

                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {item.blood_type_display}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        maxWidth: 180,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Tooltip
                        title={item.passport || t("reception.notProvided")}
                      >
                        <span>
                          {item.passport || t("reception.notProvided")}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        maxWidth: 220,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Tooltip
                        title={item.address || t("reception.notProvided")}
                      >
                        <span>
                          {item.address || t("reception.notProvided")}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {item.dosia_file_id || t("reception.notProvided")}
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {FormatDateTime(item.created_at)}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        maxWidth: 200,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Tooltip
                        title={
                          item.follow_up_details?.fullname ||
                          t("reception.notProvided")
                        }
                      >
                        <span>
                          {item.follow_up_details?.fullname ||
                            t("reception.notProvided")}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title={t("reception.delete")}>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(item.id)}
                          size={isSmallScreen ? "small" : "medium"}
                          aria-label="delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("reception.edit")}>
                        <IconButton
                          color="primary"
                          onClick={() => handleEditClick(item)}
                          size={isSmallScreen ? "small" : "medium"}
                          aria-label="edit"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination & Rows per page */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
          flexWrap="wrap"
          gap={1}
          bgcolor={theme.palette.background.paper}
          borderTop={`1px solid ${theme.palette.divider}`}
          borderRadius="0 0 12px 12px"
        >
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              displayEmpty
              aria-label={t("reception.rowsPerPage")}
            >
              {[10, 50, 100, 500, 1000].map((rows) => (
                <MenuItem key={rows} value={rows}>
                  {rows}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Pagination
            count={Math.max(Math.ceil((data?.count || 0) / rowsPerPage), 1)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      </Grid>
      <ConfirmDialog />

      {/* Edit Dialog */}
      <EditPatientDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        patient={selectedPatient}
        onSave={handleSaveEdit}
        token={token}
      />
    </Grid>
  );
};

export default PatientList;
